import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ColumnDef } from '@tanstack/react-table';

import { ApiError, api, type QcDecision, type QcQueueRow } from '@/lib/api';
import { useDateTime, useLabel, useNumber, useT, useTn } from '@/lib/i18n';
import type { StringKey } from '@/lib/strings/en';
import { notify } from '@/lib/notify';
import {
  QC_QUEUE_KEY, QC_REASON_MAX, QC_REASON_MIN, QcDecisionForm, useQcQueue,
} from '@/lib/qc';
import { PageHeader } from '@/components/page-header';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useModuleName } from '@/hooks/use-module-name';
import { usePersonaName } from '@/hooks/use-persona-name';
import { useCannotBecause } from '@/lib/permissions';
import { QcEvidencePanel } from '@/features/qc/evidence';

/**
 * QC review — the results whose scores this platform is withholding.
 *
 * WHAT THIS SCREEN CLOSES. Contract v0 §5 defines `flagged` as *"scored but held
 * for quality-control review; score is withheld until released"*, and the
 * deliverability review (B3) is blunter: it is a **human workflow**, not a
 * status enum. The server half shipped with the `/v1` surface — a run whose
 * plugin raises a QC flag goes to `flagged`, its score is withheld from all
 * three integration reads, and `POST /results/{id}/release` completes it.
 *
 * None of which a human could reach. A workflow whose only interface is a test
 * is a workflow that does not exist, and the hold would have sat there silently
 * accumulating results nobody knew were withheld.
 *
 * ### A decision now carries a reason, and it can be reversed (Workstream V2)
 *
 * Release used to be one click and no record. Both halves of that were wrong.
 *
 * **The reason.** `POST /results/:id/release` now requires one and so does
 * `/hold`; the floor is twelve characters after trimming, which is the first
 * length that will not accept "ok", "fine", "reviewed" or "looks fine" — the
 * whole vocabulary of the non-answer. The console states that floor *before* the
 * button is pressed rather than answering with a validation error afterwards: an
 * operator should not have to guess why Release is refusing them. The rule is
 * mirrored in `lib/qc.ts` and checked against the contract by a test — see the
 * comment there for why it is not imported.
 *
 * **The reversal.** A release was previously a one-way door: the run became
 * `completed`, its score started flowing to the consumer, and nothing could put
 * it back. `POST /results/:id/hold` is that undo, and it is the same capability
 * as release on purpose — splitting them would rebuild the door.
 *
 * ### A reviewer hold and a machine flag are different things
 *
 * A queue row carries `qcReview` **only when a reviewer put the run back**. A
 * machine-flagged run that no human has looked at has no such record, and that
 * absence is the signal. They are shown differently because they are different
 * jobs: one asks "is this flag real?", the other says "someone already decided
 * it was, released it, then changed their mind" — and names them, quotes their
 * reason, and dates it.
 *
 * ### Why the release button is not hidden from an analyst
 *
 * Because hiding it is not what stops them. The routes are gated by
 * `qc:release` and refuse on their own — the console's job is to surface that
 * refusal, not to substitute for it. CLAUDE.md is explicit: *"enforced
 * server-side on every request, never by hiding a control."* What
 * `plan-preproduction` R3 changed is *when* the refusal is delivered: the
 * controls are disabled and say *which role could do this*, which is the
 * question the person is really asking. They are still rendered, because hiding
 * them would teach an analyst the platform cannot do this at all.
 *
 * ### The evidence is HERE, not one screen away (Workstream V1)
 *
 * The queue used to offer a link to the score whose validity is the question,
 * which is the one place the answer is not. `GET /results/:id/qc-evidence`
 * serves the response pattern, the per-item latency and the statistic each
 * detector fired on, and **Evidence** opens it on the row — because the plan's
 * requirement is that the evidence be *"at the decision point"*, and a reviewer
 * who has to navigate away and back is a reviewer who releases on trust.
 *
 * It needs no capability. Seeing the evidence and taking the decision are
 * different acts: the route is `requireAuth` and the release routes are
 * `qc:release`, so an analyst reads the case and the button that acts on it says
 * whose job that is.
 */

/** Which way a decision points. The server has a route per direction. */
type Direction = 'released' | 'held';

/** A result this reviewer released on this screen, and may still put back. */
interface ReleasedHere {
  resultId: string;
  candidateRef: string;
  moduleId: string;
  at: string;
}

/* ------------------------------------------------------------------------ */
/*  The confirm dialog — one component, both directions                      */
/* ------------------------------------------------------------------------ */

/**
 * ONE dialog for release and re-hold, because the question asked of the
 * reviewer is identical — *why* — and the direction is already carried by which
 * route is called. The contract makes the same call: `QcDecisionBody` is one
 * schema for both.
 */
function QcDecisionDialog({
  direction, resultId, onDecided,
}: {
  direction: Direction;
  resultId: string;
  onDecided: (decision: QcDecision) => void;
}) {
  const t = useT();
  const number = useNumber();
  const [failure, setFailure] = useState<string | null>(null);

  const form = useForm<QcDecisionForm>({
    resolver: zodResolver(QcDecisionForm),
    defaultValues: { reason: '' },
  });
  // `?? ''` because `watch` reads undefined on the render that follows a reset —
  // the field is momentarily unregistered, and a counter is not worth a crash.
  const typed = form.watch('reason') ?? '';

  const decide = useMutation({
    mutationFn: ({ reason }: QcDecisionForm) =>
      direction === 'released'
        ? api.releaseResult(resultId, reason)
        : api.holdResult(resultId, reason),
    onSuccess: onDecided,
    // The server's message is the useful one — it names the state the result is
    // actually in, or says plainly that this needs another role. It belongs
    // INSIDE the dialog, beside the field, not in a toast behind it (10 §6).
    onError: (err) =>
      setFailure(err instanceof ApiError
        ? err.message
        : t(direction === 'released' ? 'qcReview.releaseFailed' : 'qcReview.holdFailed')),
  });

  /**
   * The resolver's message is a STRING KEY (see `lib/qc.ts`), so it is
   * translated here rather than arriving pre-written in English.
   */
  const problem = form.formState.errors.reason?.message as StringKey | undefined;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {t(direction === 'released' ? 'qcReview.release.title' : 'qcReview.hold.title',
            { result: resultId })}
        </DialogTitle>
        <DialogDescription>
          {t(direction === 'released'
            ? 'qcReview.release.description'
            : 'qcReview.hold.description')}
        </DialogDescription>
      </DialogHeader>

      <form
        className="grid gap-4"
        onSubmit={form.handleSubmit((values) => {
          setFailure(null);
          decide.mutate(values);
        })}
      >
        <div className="grid gap-2">
          <Label htmlFor="qc-reason">{t('qcReview.reason.label')}</Label>
          {/**
            * `Controller`, not `register`, and it is not a style choice.
            *
            * The kit's `Textarea` is a plain function component — the shadcn
            * files were ported from their React 19 form, where a function
            * component may take `ref` as a prop. On React 18 it cannot, so the
            * `ref` `register()` returns is dropped, RHF holds a field with no
            * element, and `handleSubmit` then unregisters it as unmounted:
            * the form silently validates nothing and submits nothing. A
            * controlled field needs no ref, so this is the seam that works.
            */}
          <Controller
            control={form.control}
            name="reason"
            render={({ field }) => (
              <Textarea
                id="qc-reason"
                rows={4}
                autoFocus
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={Boolean(problem)}
                aria-describedby="qc-reason-hint"
                placeholder={t('qcReview.reason.placeholder')}
              />
            )}
          />

          {/**
            * THE FLOOR IS STATED BEFORE THE REFUSAL, and stays stated after it.
            *
            * A required field whose only signal is a validation error teaches the
            * rule by refusing — which is the same failure as a 403 on a button
            * that looked available. The hint is always visible, the live count
            * makes it checkable while typing, and the error is additional.
            */}
          <p id="qc-reason-hint" className="text-muted-foreground text-xs">
            {t('qcReview.reason.hint', { min: QC_REASON_MIN })}
            {' · '}
            {/* `<bdi>` for the same reason `usePercent` wraps its output in an
                isolate: "0 of 12" is two LTR number runs around a word, and an
                un-isolated one reorders inside an Arabic paragraph. */}
            <bdi>
              {t('qcReview.reason.counter', {
                count: number(typed.trim().length),
                min: number(QC_REASON_MIN),
              })}
            </bdi>
          </p>

          {problem && (
            <p role="alert" className="text-destructive text-sm">
              {t(problem, { min: QC_REASON_MIN, max: QC_REASON_MAX })}
            </p>
          )}
        </div>

        {failure && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={decide.isPending}>
            {decide.isPending
              ? t(direction === 'released' ? 'qcReview.releasing' : 'qcReview.holding')
              : t(direction === 'released' ? 'qcReview.release' : 'qcReview.hold')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  The screen                                                               */
/* ------------------------------------------------------------------------ */

export function QcReview() {
  const queryClient = useQueryClient();
  const t = useT();
  const moduleName = useModuleName();
  const personaName = usePersonaName();
  const tn = useTn();
  // Flag names and their one-line explanations are `qcFlag.*` and
  // `qcReview.flagHint.*` in the catalogue — the same closed set the contract
  // fixes, so a reviewer reads them in their own language.
  const label = useLabel();
  /**
   * Dates follow the OPERATOR's locale, with the numerals pinned — see the
   * numerals note in `lib/i18n.tsx`. `toLocaleString()` with no argument reads
   * `navigator.language`, which is neither the operator's choice nor stable
   * between their machines.
   */
  const dateTime = useDateTime();
  // The same query key the sidebar badge reads, so the count beside the nav item
  // and the rows on this screen cannot disagree.
  const queue = useQcQueue();
  // null when the caller may decide; otherwise the sentence naming who can.
  const cannotRelease = useCannotBecause('qc:release');

  /** Which decision is being confirmed, if any. */
  const [pending, setPending] = useState<{ direction: Direction; resultId: string } | null>(null);

  /** Which result's evidence is open, if any (V1). */
  const [evidenceFor, setEvidenceFor] = useState<string | null>(null);

  /**
   * What this reviewer released while on this screen.
   *
   * A released run is `completed`, so it LEAVES the queue — which means the undo
   * would otherwise be unreachable from the one screen where somebody has just
   * decided they want it. This is deliberately session-local and says so: it is
   * the window immediately after a decision, not a stored list, and a reload
   * clears it. Nothing here is a second source of truth; the row itself comes
   * back from the server the moment it is held again.
   */
  const [releasedHere, setReleasedHere] = useState<ReleasedHere[]>([]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: QC_QUEUE_KEY });
    // The run changed state, so anything counting states is now stale.
    void queryClient.invalidateQueries({ queryKey: ['assessments'] });
    void queryClient.invalidateQueries({ queryKey: ['results'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const onDecided = (decision: QcDecision) => {
    const row = queue.data?.items.find((i) => i.resultId === decision.resultId);

    if (decision.qcReview.decision === 'released') {
      notify.success(
        t('qcReview.released'),
        t('qcReview.released.detail', { result: decision.resultId }),
      );
      setReleasedHere((current) => [
        {
          resultId: decision.resultId,
          candidateRef: row?.candidateRef ?? decision.assessmentId,
          moduleId: row?.moduleId ?? '',
          at: decision.qcReview.at,
        },
        ...current.filter((r) => r.resultId !== decision.resultId),
      ]);
    } else {
      notify.success(
        t('qcReview.heldAgain'),
        t('qcReview.heldAgain.detail', { result: decision.resultId }),
      );
      setReleasedHere((current) => current.filter((r) => r.resultId !== decision.resultId));
    }

    setPending(null);
    refresh();
  };

  const columns = useMemo<ColumnDef<QcQueueRow, unknown>[]>(
    () => [
      {
        header: t('qcReview.column.candidate'),
        accessorKey: 'candidateRef',
        cell: ({ row }) => (
          <div>
            {/* An opaque reference, and the only thing about a person this
                platform holds. Never a name. */}
            <p className="font-mono text-sm" dir="ltr">{row.original.candidateRef}</p>
            <p className="text-muted-foreground text-xs">{personaName(row.original.personaId)}</p>
          </div>
        ),
      },
      {
        header: t('questions.column.module'),
        accessorKey: 'moduleId',
        cell: ({ row }) => (
          <div>
            <p className="text-sm">{moduleName(row.original.moduleId)}</p>
            <p className="text-muted-foreground text-xs">
              {t('modules.configVersion', { version: row.original.configVersion })}
            </p>
          </div>
        ),
      },
      {
        header: t('qcReview.column.why'),
        accessorKey: 'qcFlags',
        cell: ({ row }) => {
          /**
           * `qcReview` is present ONLY on a run a reviewer put back. Its absence
           * is what says "no human has decided about this yet" — so the row
           * leads with which of the two it is, rather than making a reviewer
           * infer it from a field that is not there.
           */
          const review = row.original.qcReview;
          const byReviewer = review?.decision === 'held';

          return (
            <div className="space-y-1.5">
              <Badge variant={byReviewer ? 'warning' : 'secondary'}>
                {t(byReviewer ? 'qcReview.source.reviewer' : 'qcReview.source.machine')}
              </Badge>

              {byReviewer && review && (
                <div className="border-s-2 ps-2">
                  {/* The rationale in the reviewer's own words. It is why the
                      floor exists: "ok" here would tell the next person nothing. */}
                  <p className="text-sm">{review.reason}</p>
                  <p className="text-muted-foreground text-xs">
                    {t('qcReview.reviewerBy', {
                      actor: review.actor,
                      at: dateTime(review.at),
                    })}
                  </p>
                </div>
              )}

              {/* The machine's findings stay visible either way: a re-hold does
                  NOT touch `qcFlags`, because the envelope is the immutable
                  record of what the instrument found. */}
              {row.original.qcFlags.map((flag) => {
                const hint = label('qcReview.flagHint', flag);
                return (
                  <div key={flag}>
                    <Badge variant="destructive">{label('qcFlag', flag)}</Badge>
                    {hint !== flag && (
                      <p className="text-muted-foreground mt-0.5 text-xs">{hint}</p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        header: t('qcReview.column.scored'),
        accessorKey: 'scoredAt',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {row.original.scoredAt ? dateTime(row.original.scoredAt) : t('common.notAvailable')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            {/* V1. The evidence, on the row — no capability, because reading the
                case and deciding it are different acts. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEvidenceFor(row.original.resultId)}
            >
              {t('qcEvidence.action')}
            </Button>
            {/* The score is withheld from the consumer, not from the reviewer —
                a queue that cannot show what it is asking someone to judge is
                not a queue. */}
            <Button variant="outline" size="sm" asChild>
              <Link to={`/results/${row.original.resultId}`}>{t('qcReview.reviewResult')}</Link>
            </Button>
            <Button
              size="sm"
              disabled={cannotRelease !== null}
              // The reason travels WITH the disabled state — a greyed control
              // with no explanation is worse than the 403 it replaced, because
              // the 403 at least carried a message.
              title={cannotRelease ?? undefined}
              onClick={() => setPending({ direction: 'released', resultId: row.original.resultId })}
            >
              {t('qcReview.release')}
            </Button>
          </div>
        ),
      },
    ],
    [t, label, dateTime, cannotRelease, moduleName, personaName],
  );

  const rows = queue.data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.qcReview')}
        description={t('qcReview.description')}
        actions={
          queue.data && (
            <Badge variant={rows.length > 0 ? 'destructive' : 'default'}>
              {rows.length > 0 ? tn('qcReview.held', rows.length) : t('qcReview.nothingHeld')}
            </Badge>
          )
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        isLoading={queue.isLoading}
        error={queue.error}
        onRetry={() => void queue.refetch()}
        emptyTitle={t('qcReview.empty.title')}
        emptyDescription={t('qcReview.empty.description')}
      />

      {releasedHere.length > 0 && (
        <section className="mt-8 space-y-3" aria-labelledby="qc-released-here">
          <div>
            <h2 id="qc-released-here" className="font-medium">
              {t('qcReview.releasedHere.title')}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t('qcReview.releasedHere.description')}
            </p>
          </div>

          <ul className="divide-y rounded-md border">
            {releasedHere.map((released) => (
              <li key={released.resultId} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm" dir="ltr">{released.candidateRef}</p>
                  <p className="text-muted-foreground text-xs">
                    {released.moduleId ? `${moduleName(released.moduleId)} · ` : ''}
                    {dateTime(released.at)}
                  </p>
                </div>
                <Button variant="outline" asChild size="sm">
                  <Link to={`/results/${released.resultId}`}>{t('qcReview.reviewResult')}</Link>
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  // Identically gated: putting a score back under review is the
                  // same power as letting it out.
                  disabled={cannotRelease !== null}
                  title={cannotRelease ?? undefined}
                  onClick={() => setPending({ direction: 'held', resultId: released.resultId })}
                >
                  {t('qcReview.hold')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rows.length > 0 && (
        <p className="text-muted-foreground mt-4 text-sm">
          {t('qcReview.footnote')}{' '}
          <Link to="/settings" className="underline">
            {t('qcReview.footnote.link')}
          </Link>
          .
        </p>
      )}

      {/**
        * THE EVIDENCE, AND THE DECISION IT SUPPORTS, IN THAT ORDER (V1).
        *
        * Release lives in this dialog's footer as well as on the row, because
        * the moment a reviewer has actually read the case is the moment they
        * decide — and sending them back to the table to find the row again is
        * how a considered decision becomes a remembered one. Pressing it closes
        * the evidence and opens the confirm dialog, so the two are never nested:
        * one Radix dialog at a time, and the rationale is still required.
        */}
      <Dialog open={evidenceFor !== null} onOpenChange={(open) => !open && setEvidenceFor(null)}>
        {evidenceFor && (
          <DialogContent className="max-h-[85vh] sm:max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('qcEvidence.title', { result: evidenceFor })}</DialogTitle>
              <DialogDescription>{t('qcEvidence.description')}</DialogDescription>
            </DialogHeader>

            <QcEvidencePanel resultId={evidenceFor} />

            <DialogFooter>
              <Button
                // Identically gated to the row's button. The route refuses on
                // its own regardless; this says whose job it is beforehand.
                disabled={cannotRelease !== null}
                title={cannotRelease ?? undefined}
                onClick={() => {
                  const resultId = evidenceFor;
                  setEvidenceFor(null);
                  setPending({ direction: 'released', resultId });
                }}
              >
                {t('qcReview.release')}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Mounted only while open, so each decision starts with an empty reason —
          a rationale left over from the previous result would be worse than no
          rationale at all. */}
      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        {pending && (
          <QcDecisionDialog
            key={`${pending.direction}:${pending.resultId}`}
            direction={pending.direction}
            resultId={pending.resultId}
            onDecided={onDecided}
          />
        )}
      </Dialog>
    </div>
  );
}
